/**
 * PATH: packages/ui/src/components/find/findMatchScan.ts
 * FILE_ID: UI-FIND-MATCH-SCAN-001
 * PURPOSE: Pure scanning primitives shared by both in-page find
 *          highlighters — normalise the needle, walk a container's
 *          readable text nodes, and list the hit offsets inside one
 *          text node.
 * WHY:     The <mark> highlighter (findHighlightDom) and the mutation-free
 *          Range highlighter (findHighlightRanges) must agree EXACTLY on
 *          what counts as a hit, or the count in the find bar would differ
 *          from what the browser paints. One scanner, two painters.
 * FLOW:
 *   ┌───────────────┐   ┌──────────────────┐   ┌──────────────┐
 *   │ container +   │ → │ TreeWalker over  │ → │ Text[] +     │
 *   │ raw query     │   │ readable text    │   │ hit offsets  │
 *   └───────────────┘   └──────────────────┘   └──────────────┘
 * DEPENDENCIES: none (pure DOM reads; testable in jsdom).
 * RELATED:
 *   - findHighlightDom.ts: the <mark> painter (legacy fallback).
 *   - findHighlightRanges.ts: the CSS Custom Highlight painter.
 */

/**
 * Never descend into these — their text is not user-readable prose, and
 * rewriting an <input>/<textarea> value node would corrupt form state.
 */
export const FIND_SKIP_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'NOSCRIPT',
  'TEXTAREA',
  'INPUT',
  'SELECT',
  'MARK',
])

/** Literal, case-insensitive, trimmed. Empty when the query is blank. */
export function normaliseNeedle(rawQuery: string): string {
  return rawQuery.trim().toLowerCase()
}

/**
 * Collect every readable text node inside `container` that contains
 * `needle`. Collected up-front (never mid-traversal) so a caller is free
 * to mutate the nodes it gets back.
 */
export function collectMatchTextNodes(
  container: HTMLElement,
  needle: string,
): Text[] {
  if (!needle) return []
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = (node as Text).parentElement
      if (!parent || FIND_SKIP_TAGS.has(parent.nodeName)) return NodeFilter.FILTER_REJECT
      const value = node.nodeValue
      if (!value || !value.toLowerCase().includes(needle)) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })
  const targets: Text[] = []
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    targets.push(node as Text)
  }
  return targets
}

/** Start offsets of every non-overlapping hit of `needle` inside `value`. */
export function matchOffsets(value: string, needle: string): number[] {
  if (!needle) return []
  const lower = value.toLowerCase()
  const offsets: number[] = []
  let hit = lower.indexOf(needle)
  while (hit >= 0) {
    offsets.push(hit)
    hit = lower.indexOf(needle, hit + needle.length)
  }
  return offsets
}
